import { Component, OnDestroy, ViewChild, TemplateRef, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';

import { Subscription, forkJoin, Observable, filter, map } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../../../Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../../../Common/Forms/enums/table-column-input-type.enum';
import { TargetingListService } from '../../../services/targeting-list.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { ChipModel } from '../../../../../Common/Forms/models/chip.model';
import { UsersService } from '../../../../../Core/Users/services/users.service';
import { DateHelper } from '../../../../../Common/Forms/helpers/date.helper';
import { TargetingListsApiService } from '../../../../../api/targeting/targeting-lists.api.service';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { DataRow } from '../../../../../Common/RhpContexts/components/DistPickerDialog/dist-picker-dialog.component';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { EntityIdApiModel } from '../../../../../api/targeting/model/entity-id-api-model';
import { EntityTypeDefaultImageEnum } from '../../../../../Common/Misc/enums/entity-type-default-image.enum';
import { DistributionStatus } from '@Common/TargetingShared/enum/distribution-status.enum';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { DistributionApi } from '@api/distributions/distribution.api';


@Component({
    selector: 'agpr-manage-dist-dialog',
    templateUrl: './manage-dist-dialog.component.html',
    styleUrls: ['./manage-dist-dialog.component.scss'],
    standalone: false
})
export class ManageDistDialogComponent extends AbstractDialogComponent implements OnDestroy, AfterViewInit {

    @Input() set setEntity(entity: TargetingResultModel) {
        this.focusedEntity = entity;
        this.entityDistKey = this.focusedEntity?.entityType === EntityType.CONTACT ? 'contactUuid' : 'outletUuid';

        this.subscriptions.push(
            this.targetingListsApiService.getListMemberships([{
                [this.entityDistKey]: this.focusedEntity?.id
            }]).subscribe(listMemberships => {
                this.listMembershipIds = listMemberships[0]?.targetingLists?.map(list => list.id) || [];
                this.populateListMembershipInitialSelection();
            })
        );
    }

    @Output() listMembershipChanged: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('countInfoCell') public countCellTemplate: TemplateRef<any>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;

    subscriptions: Subscription[] = [];

    id = 'manageDist';
    loading: boolean = false;
    listMembershipIds: number[];
    focusedEntity: TargetingResultModel;
    imageDefault = EntityTypeDefaultImageEnum;

    dataRows: DataRow[];
    filteredRows: DataRow[] = [];
    freshCreatedId: number;

    initialChips: ChipModel[] = [];
    distSummaryChips: ChipModel[] = [];
    addedDist: string[] = [];
    removedDist: string[] = [];
    entityDistKey: string;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.checkbox,
        heading: 'Discover.distributionBuilder.table.distributionName',
        labelField: 'name',
        field: 'selected',
        sortable: true,
        sortType: 'text',
        sortField: 'name',
        width: 185,
    };

    columnDefs: TableColumnModel[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public usersService: UsersService,
        private messageService: MessageService,
        private targetingListsApiService: TargetingListsApiService,
        private distributionApi: DistributionApi,
        public distributionService: DistributionService,
    ) {
        super(coreDialogService, 'ManageDistribution');

        this.subscriptions.push(

            this.distributionService.getDistributionSummaries().pipe(
                filter(x => !!x),
                map(dist => dist.filter(element => element.status === DistributionStatus.draft))
            ).subscribe(dists => {

                this.dataRows = dists.map(list => {
                    return <DataRow>{
                        id: list.id,
                        selected: !!this.distSummaryChips.find( x => x.label === list.name),
                        name: list.name,
                        // count: list.count,
                        originalData: list,
                        createdDate: DateHelper.toDateString(list.createdDate),
                        modifiedDate: DateHelper.toDateString(list.modifiedDate),
                    };
                });
                this.filteredRows = this.dataRows;

                this.fetchUserNames();

                // Now that the creation completed and we received the new list,
                // Auto select the list that the user just created
                if (this.freshCreatedId) {
                    const freshCreatedRow = this.dataRows.find(x => x.id === this.freshCreatedId);
                    if (freshCreatedRow) {
                        freshCreatedRow.selected = true;
                        this.distSummaryChips.push(<ChipModel>{
                            label : freshCreatedRow.name
                        });
                    }
                    this.updateChangeReceipt();
                    this.freshCreatedId = undefined;
                }
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.columnDefs = [this.nameColumn];
        });
    }

    removeChip(index: number) {
        const chipName = this.distSummaryChips[index]?.label;
        this.distSummaryChips.splice(index, 1);
        this.dataRows.find(x => x.name === chipName).selected = false;
        this.updateChangeReceipt();
    }

    private populateListMembershipInitialSelection() {
        if (this.dataRows && this.listMembershipIds.length > 0) {
            this.listMembershipIds.forEach(id => {
                const list = this.dataRows.find(x => x.originalData?.individualRecipientsListId === id);
                if (list) {
                    list.selected = true;
                    this.initialChips.push(<ChipModel>{label: list.name});
                    this.distSummaryChips.push(<ChipModel>{label: list.name});
                }
            });
        }
    }

    /**
     * Compare initial list membership state with current state, updating addedDist & removedDist with changes
     */
    private updateChangeReceipt() {
        this.distSummaryChips.forEach(list => {
            if (!this.initialChips.find(x => x.label === list.label) && !this.addedDist.includes(list.label)) {
                this.addedDist.push(list.label);
            }
        });

        this.initialChips.forEach(list => {
            if (!this.distSummaryChips.find(x => x.label === list.label) && !this.removedDist.includes(list.label)) {
                this.removedDist.push(list.label);
            }
        });

        this.addedDist.forEach((list, index) => {
            if (!this.distSummaryChips.find(x => x.label === list)) {
                this.addedDist.splice(index, 1);
            }
        });

        this.removedDist.forEach((list, index) => {
            if (this.distSummaryChips.find(x => x.label === list)) {
                this.removedDist.splice(index, 1);
            }
        });

        // update array reference for JoinList pipe
        this.addedDist = this.addedDist.slice();
        this.removedDist = this.removedDist.slice();
    }

    updateDists() {
        this.loading = true;
        const apiCalls: Observable<void>[] = [];

        const distsToAdd: DistributionSummaryModel[] = this.dataRows
            .filter(x => this.addedDist.includes(x.name))
            .map(x => x.originalData);

        const distsToRemove: DistributionSummaryModel[] = this.dataRows
            .filter(x => this.removedDist.includes(x.name))
            .map(x => x.originalData);

        if (distsToAdd.length > 0) {
            const entityApiModel: EntityIdApiModel = {
                [this.entityDistKey]: this.focusedEntity.id
            };

            if (
                this.focusedEntity.selectedEmployment &&
                this.focusedEntity.selectedEmployment.id
            ) {
                entityApiModel.employmentId = this.focusedEntity.selectedEmployment.id;
            }

            apiCalls.push(this.distributionApi.addEntitiesToDist(
                distsToAdd.map(x => x.id),
                [entityApiModel]
            ));
        }

        distsToRemove.forEach(dist => {
            apiCalls.push(
                this.targetingListsApiService.removeEntitiesFromList(
                    dist.individualRecipientsListId, [<EntityIdApiModel>{[this.entityDistKey]: this.focusedEntity.id}]
                )
            );
        });

        forkJoin(apiCalls).subscribe({
            next: () => {
                this.loading = false;

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.manageDist.success.heading',
                    detail: 'Discover.manageDist.success.message',
                });

                // reload?
                this.closeDialog();
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.manageDist.failed.heading',
                    detail: 'Discover.manageDist.failed.message',
                });
                this.closeDialog();
            }
        });
    }

    selectionChanged(item: DataRow) {
        if (item.selected) {
            this.distSummaryChips.push(<ChipModel>{ label: item.name });
        } else {
            this.distSummaryChips.splice(this.distSummaryChips.findIndex(x => x.label === item.name), 1);
        }
        this.distSummaryChips = this.distSummaryChips.slice();
        this.updateChangeReceipt();
    }

    selectAll(state: boolean) {
        if (state) {
            this.distSummaryChips = this.filteredRows.map(x => <ChipModel>{ label: x.name });
            this.addedDist = this.filteredRows.map(x => x.name);
            this.removedDist = [];
        } else {
            this.distSummaryChips = [];
            this.addedDist = [];
            this.removedDist = this.initialChips.map(x => x.label);
        }
    }

    resetDists() {
        this.filteredRows = this.dataRows;
    }

    filterRows(searchString: string) {
        if (searchString.length === 0) {
            this.filteredRows = this.dataRows;
        }
        this.filteredRows = this.dataRows.filter(row => row.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
    }

    /**
     * Converts user ids to usernames for the info tooltip
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId), ...this.dataRows.map(x => +x.originalData.modifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdUser = creator.getDisplayName();
                    }

                    const modifier = users.find(x => x.id === row.originalData.modifiedByUserId);
                    if (modifier) {
                        row.modifiedUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
